import { useCallback, useState } from "react"
import { AxiosResponse } from "axios"
import { useMutation } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import {
  callAdminApi,
  fetchUnits,
  fetchAiUsageMetrics,
  RequestType,
  AiUsageMetricsResponse,
} from "api/Superadmin/superadmin"
import { ServerId, serverApiBase } from "utils/server"

// Query parameters type for AI usage metrics
export interface AiUsageMetricsQueryParams {
  period?: "today" | "all-time" | string
  user?: string
  unit?: string
  top?: "users" | "units"
  limit?: number
}

// Define the return type of the hook
interface UseSuperadminReturn {
  getSequenceUnits: () => Promise<AxiosResponse<string>>
  isLoadingSequenceUnits: boolean
  getUnitsForServer: (server: ServerId) => Promise<string[]>
  makeAdminApi: (
    route: string,
    requestType: RequestType,
    body?: string
  ) => Promise<AxiosResponse<unknown>>
  isCallingAdminApi: boolean
  getAiUsageMetrics: (
    queryParams?: AiUsageMetricsQueryParams
  ) => Promise<AxiosResponse<AiUsageMetricsResponse>>
  isLoadingAiMetrics: boolean
}

/**
 * Superadmin operations — imperative admin commands and reads, built on
 * `useAuthedAxios` (which also covers the cross-server unit listing via its
 * baseURL parameter).
 */
export const useSuperadmin = (): UseSuperadminReturn => {
  const getAxios = useAuthedAxios()

  const [isLoadingSequenceUnits, setIsLoadingSequenceUnits] = useState(false)
  const [isLoadingAiMetrics, setIsLoadingAiMetrics] = useState(false)

  const getSequenceUnits = useCallback(async (): Promise<
    AxiosResponse<string>
  > => {
    setIsLoadingSequenceUnits(true)
    try {
      return await fetchUnits(await getAxios())
    } finally {
      setIsLoadingSequenceUnits(false)
    }
  }, [getAxios])

  const getAiUsageMetrics = useCallback(
    async (
      queryParams?: AiUsageMetricsQueryParams
    ): Promise<AxiosResponse<AiUsageMetricsResponse>> => {
      setIsLoadingAiMetrics(true)
      try {
        return await fetchAiUsageMetrics(await getAxios(), queryParams)
      } finally {
        setIsLoadingAiMetrics(false)
      }
    },
    [getAxios]
  )

  const adminApiMutation = useMutation({
    mutationFn: async (args: [string, RequestType, string?]) =>
      callAdminApi(await getAxios(), ...args),
  })

  const makeAdminApi = useCallback(
    (route: string, requestType: RequestType, body?: string) =>
      adminApiMutation.mutateAsync([route, requestType, body]),
    [adminApiMutation.mutateAsync]
  )

  /**
   * Fetches the available units of a specific server by calling that server's
   * conductor directly (cross-origin). Superadmin tokens are accepted by every
   * server's conductor, so this works from any server. Requires the target
   * conductor to allow this origin via CORS.
   */
  const getUnitsForServer = useCallback(
    async (server: ServerId): Promise<string[]> => {
      const instance = await getAxios(serverApiBase(server))
      const res = await fetchUnits(instance)
      return res.data.split("\n").filter((unit) => unit.trim() !== "")
    },
    [getAxios]
  )

  return {
    getSequenceUnits,
    isLoadingSequenceUnits,
    getUnitsForServer,
    makeAdminApi,
    isCallingAdminApi: adminApiMutation.isPending,
    getAiUsageMetrics,
    isLoadingAiMetrics,
  }
}
